/**
 * Copy for the Audit surface (SPEC §8; AUD-1..AUD-7).
 *
 * Sentence case throughout — `Eyebrow` and `Badge` apply their own casing.
 * Anything interpolating a row's value is a **function**, because a per-row
 * control needs an accessible name a screen reader can tell from its
 * neighbours.
 *
 * **`actions` hand-mirrors `AUDIT_ACTIONS` in `apps/api/src/types/domain.ts`.**
 * There is no shared package between the two workspaces, so keep them in step by
 * hand — and note that the schema in `lib/api.ts` deliberately does **not**
 * enforce the union: AUD-1 makes every row permanent and AUD-6's catalog grows
 * with the spec, so `actionLabel` falls back to the raw value rather than a log
 * refusing to render a row it does not recognise.
 */
export const AUDIT_COPY = {
  eyebrow: 'Audit',
  title: 'Audit log',

  /**
   * The lead line. It names both halves of AUD-3, because "my agency's log" is
   * the half people expect and the other-agency half is the one that surprises.
   */
  summary:
    'Everything your agency did, and every time another agency opened or downloaded something you shared.',

  columns: {
    occurredAt: 'When',
    action: 'Event',
    actor: 'Who',
    target: 'Subject',
    space: 'Space',
    details: 'Details',
  },

  filters: {
    eventClass: {
      label: 'Show',
      all: 'Everything',
      agency: 'Our actions',
      counterpart: 'Another agency’s access',
    },
    space: {
      label: 'Space',
      all: 'Every space',
    },
    actor: {
      label: 'Person',
      all: 'Everyone',
    },
  },

  /**
   * The class badge on a row. Sentence case; `Badge` uppercases it.
   *
   * **The key is the wire value and the label is not.** `eventClass` is
   * `'agency' | 'counterpart'` on the API and in the `?class=` filter, and it
   * stays that way — it is a request contract, and SPEC §8 is where that would
   * be renamed. What a reader is *shown* is a different question, and
   * "Counterpart" answers it in the vocabulary of a pair: a room holds up to
   * twenty-five agencies (A-10), so the fact the badge states is that somebody
   * who is not us did this.
   */
  classes: {
    agency: 'Ours',
    counterpart: 'Another agency',
  },

  /**
   * One label per `AUDIT_TARGET_TYPES` member.
   *
   * **`link` and `space` are two keys reading the same word, and that is the
   * correct rendering rather than a duplication to tidy away.** M14 renamed the
   * concept and AUD-1 makes every row permanent, so the log holds both spellings
   * of one kind of thing forever — and to somebody reading the log they *are*
   * one kind of thing. The rename reached this word before it reached the key
   * beside it, which is how `space` came to be missing while the API wrote it.
   */
  targets: {
    file: 'File',
    folder: 'Folder',
    user: 'User',
    link: 'Space',
    space: 'Space',
    agency: 'Agency',
  },

  /**
   * The AUD-6 catalog, one label each, **in `AUDIT_ACTIONS`' own class order**
   * so the two can be read side by side.
   *
   * **This map had drifted 37 actions behind the union** and the sweep closed
   * it. The membership five (`space.member_*`) were the visible half — an
   * agency joining a room, being removed from one, or having its visibility
   * changed rendered as `space.member_accepted` on an evidence log — but the
   * whole tail of the catalog was missing with them: caps, plans, retention and
   * tiering, the credential lifecycle, notifications, and all four exit
   * actions. Every one of them fell through `actionLabel`'s raw-value fallback,
   * which is correct behaviour for an action a *deployed* reader has never
   * heard of and quite wrong as the resting state of the map.
   *
   * `apps/api/src/types/auditCopy.test.ts` is the instrument that would have
   * caught it, and it fails now if an action is added to the union without a
   * label landing here in the same change.
   */
  actions: {
    // Agency & spaces
    'agency.invited': 'Agency invited',
    'agency.created': 'Agency created',
    // OPS-19 — the operator removing an agency created in error.
    'agency.deleted': 'Agency deleted',
    'agency.provisioning_requested': 'Subscription requested',
    'agency.storage_provisioned': 'Storage provisioned',
    'agency.provisioning_failed': 'Provisioning failed',
    'agency.subscribed': 'Agency subscribed',
    'agency.type_set': 'Agency type set',
    // CON-27, OPS-18. Read from the seat of the agency whose list changed —
    // "Connected to an agency" rather than "Agencies connected", because the row
    // is a fact about this agency's own list and the direction is the point.
    'agency.connected': 'Connected to an agency',
    'agency.disconnected': 'Disconnected from an agency',
    'space.created': 'Space created',
    'space.renamed': 'Space renamed',
    'space.window_changed': 'Space expiry window changed',
    // CON-26. "Space deleted" and not "Space closed": the room is gone, and a
    // softer word would suggest something that could be reopened. This is the
    // last row this log will ever carry about that space, and its Space column
    // names a room nobody can now open — which is the honest reading and is why
    // AUD-1 keeps the rows above it.
    'space.deleted': 'Space deleted',
    // Worded from the room's seat rather than the agency's, because the Space
    // column beside them already names which room — "Joined a space" would
    // repeat it, and an admin reading down this column is asking *who* moved.
    'space.member_invited': 'Agency invited to space',
    'space.member_accepted': 'Agency joined space',
    'space.member_removed': 'Agency left space',
    // CON-24, and the admin app's `fleetAuditLog.copy.ts` uses the same words
    // for the same row. Named as what moved rather than where it landed: the
    // row carries both values and the Details cell shows them, so "Agency
    // scoped to own" would be a shorter second copy of one of the two, and
    // wrong on every row that widens.
    'space.member_scope_changed': 'Agency visibility changed',
    'space.member_upgraded': 'Agency can now contribute',
    // Admin & users
    'member.invited': 'Member invited',
    'member.activated': 'Member activated',
    'member.revoked': 'Member revoked',
    'member.reactivated': 'Member reactivated',
    'admin.designated': 'Admin designated',
    'admin.revoked': 'Admin rights removed',
    'member.link_granted': 'Space access granted',
    'member.link_revoked': 'Space access removed',
    'member.link_access_changed': 'Space access level changed',
    // "Enrollment", the word the Users surface puts on the control itself
    // (USR-14) — not the API's own "email sign-in", which names a mechanism a
    // reader of this log never chose.
    'member.enrollment_unlocked': 'Enrollment unlocked',
    'member.enrollment_locked': 'Enrollment locked',
    // Credential lifecycle (SYS-8, USR-11, USR-12). No sign-in row exists to
    // label — §8 records why there is deliberately no `session.started`.
    'passkey.enrolled': 'Passkey added',
    'passkey.removed': 'Passkey removed',
    'passkey.reset': 'Passkeys reset',
    // UPL-13's two. Read by an agency admin looking at their own log, so they
    // name the thing rather than the mechanism: "scanner", not "ingest
    // credential". Whoever added it is Blue Partner, which the actor column
    // already says.
    'ingest.scanner_created': 'Scanner added',
    'ingest.scanner_revoked': 'Scanner revoked',
    'signature.saved': 'Signature saved',
    'signature.removed': 'Signature removed',
    // NTF-1. A subscription is a person asking to be told, so the label is
    // about them rather than about the mail.
    'notification.subscribed': 'Notifications turned on',
    'notification.unsubscribed': 'Notifications turned off',
    // Files
    'folder.created': 'Folder created',
    'folder.renamed': 'Folder renamed',
    'folder.deleted': 'Folder deleted',
    'file.upload_started': 'Upload started',
    'file.uploaded': 'File uploaded',
    'file.hash_computed': 'Hash computed',
    'file.integrity_mismatch': 'Integrity mismatch',
    'file.downloaded': 'File downloaded',
    'file.deleted': 'File deleted',
    'file.restored': 'File restored',
    'file.purged': 'File purged',
    // Access
    'space.opened': 'Space opened',
    // Caps (PLN-10, PLN-11, PLN-12)
    'agency.cap_warned': 'Approaching a cap',
    'agency.cap_exceeded': 'Cap exceeded',
    'agency.download_throttled': 'Download throttled',
    // Plans & billing (PLN-5, PLN-13, PLN-15, RET-8)
    'agency.trial_started': 'Trial started',
    'agency.plan_changed': 'Plan changed',
    'agency.rebanded': 'Band changed',
    'agency.retention_purchased': 'Retention purchased',
    'agency.retention_released': 'Retention released',
    // Retention & lifecycle (PLN-6, RET-2, RET-3, RET-5, RET-9, RET-11).
    // **"Retrieval" and "archived", never "rehydrate" and "Archive tier"** —
    // `folderView.copy.ts` argues it, and a log an agency exports as evidence
    // is the last place to introduce a second vocabulary for one act.
    'file.rejected_size': 'File too large',
    'file.expiry_warned': 'Expiring soon',
    'file.expired': 'File expired',
    'file.retention_set': 'Retention set',
    'file.tier_changed': 'Storage tier changed',
    'file.rehydration_requested': 'Retrieval requested',
    'file.rehydrated': 'File retrieved',
    // Exit (EXIT-2, EXIT-4, EXIT-7)
    'agency.exit_initiated': 'Exit started',
    'agency.billing_transferred': 'Billing transferred',
    'agency.exit_completed': 'Exit completed',
    'space.member_exit_notified': 'Agency is leaving the service',
    /*
      **Retired actions with no writer left, and the labels stay** (AUD-6 as
      amended, **ADR-0072**). AUD-1 forbids editing or deleting a row, so every
      event ever written under these strings is still in its partition and still
      renders on this surface. Retiring an action retires it as something new
      code may *write*, never as something the log may *hold* — a reader that
      dropped an unrecognised action, or showed it as a raw string, would be
      quietly rewriting the history AUD-1 exists to keep. The asymmetry is the
      correct end state rather than a leftover, and it is why these are not
      deleted along with the endpoints that wrote them.

      `link.counterpart_exit_notified` joined them in M14's sweep, which took
      SPEC §8's `space.member_exit_notified` for the live action — the last
      `link.*` string in the union. Its label is deliberately identical to the
      new one's: the two spell one fact, and a reader scrolling past the
      changeover should see no seam.
    */
    'link.requested': 'Connection requested',
    'link.accepted': 'Connection accepted',
    'link.severed': 'Connection severed',
    'link.upgraded_bidirectional': 'Space became two-way',
    'link.counterpart_exit_notified': 'Agency is leaving the service',
    'recipient.added': 'Recipient added',
    'recipient.removed': 'Recipient removed',
    'recipient.migrated': 'Recipient migrated',
  } as Record<string, string>,

  /** Filter to one person, from their row. */
  actorFilter: (email: string) => `Filter to ${email}`,
  /** No space on this row: `member.invited` and its kind name none. */
  noSpace: '-',

  details: {
    open: (action: string) => `Details of ${action}`,
    label: 'View',
    /** Field labels in the drawer. */
    fields: {
      occurredAt: 'Occurred at',
      action: 'Event',
      actor: 'Who',
      agency: 'Agency',
      eventClass: 'Class',
      target: 'Subject',
      space: 'Space',
      eventId: 'Event id',
      payload: 'Recorded detail',
    },
    none: 'No further detail was recorded.',
    close: 'Close details',
  },

  /**
   * AUD-6a, said once where a reader can act on it.
   *
   * A `space.opened` row's timestamp is the first open of that UTC day, and its
   * position in the list is the start of that day — so it can sit below rows
   * that are older than it. Without this the reader concludes the sort is
   * broken, which is the one thing an evidence log cannot afford.
   */
  dayBucketed: {
    marker: 'Once a day',
    explanation:
      'Recorded once per person, per space, per UTC day. The time is that day’s first open; the row is ordered as of the start of the day (AUD-6a).',
  },

  loadMore: {
    label: 'Load older events',
    working: 'Loading…',
    /**
     * The empty-page case. A page with no rows and more to read is not the end
     * (ADR-0015), so the button stays and says what happened.
     */
    none: 'No matching events in the last stretch of the log. Keep going to search further back.',
  },

  /**
   * EXIT-6's export.
   *
   * **The label names the audit log.** A reader
   * looking at a filtered table reasonably assumes a nearby export button exports
   * what they are looking at. It does not, and it must not — the export is the
   * copy an agency keeps.
   */
  export: {
    action: 'Export audit log',
  },

  states: {
    loading: 'Loading the audit log…',
    empty: 'No events match. The log records what happens from here on.',
    tableCaption: 'Audit events, newest first',
    error: 'The audit log could not be loaded.',
    forbidden: 'Only an agency admin can read the audit log.',
    retry: 'Try again',
  },

  dateLocale: 'en-US',
} as const;
